import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import {
  Plus, Upload, Settings, Search, Calendar, X, Tag,
  Activity, ChevronDown, DollarSign, FileText, Receipt,
} from 'lucide-react'

import { api, ApiError } from '@/lib/api/client'
import type {
  Subscription, SubscriptionLine, SubscriptionDiscount, Customer,
  SubscriptionEvent,
} from '@/types/api'
import {
  AddLineModal, ApplyDiscountModal, ChangePriceModal, EndLineModal,
  NewSubscriptionModal, PauseSubscriptionModal,
} from './BillingModals'
import { LineInvoiceHistoryDrawer } from './LineInvoiceHistoryDrawer'

// ── Constants ────────────────────────────────────────────────────────────────

type TabKey = 'lines' | 'discounts' | 'history' | 'settings'
const TABS: { key: TabKey; label: string }[] = [
  { key: 'lines',     label: 'Lines'     },
  { key: 'discounts', label: 'Discounts' },
  { key: 'history',   label: 'History'   },
  { key: 'settings',  label: 'Settings'  },
]

// Product-type → display color. Matches design (OKLCH, brand teal + clem orange).
const PRODUCT_TYPE_COLOR: Record<string, string> = {
  SYNC:    'oklch(0.62 0.13 50)',     // clem orange
  REPORT:  'oklch(0.55 0.16 258)',    // blue
  API:     'oklch(0.55 0.15 295)',    // purple
}

function productColor(pt: string | null | undefined): string {
  if (!pt) return 'var(--ns-text-muted)'
  return PRODUCT_TYPE_COLOR[pt] ?? 'var(--ns-brand-strong)'
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtMoney(n: number | null | undefined, currency = 'SEK'): string {
  if (n == null) return '—'
  return new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 2 }).format(n) + ' ' + currency
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—'
  return new Date(s + 'T00:00:00Z').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).map(p => p[0]).join('').toUpperCase().slice(0, 2)
}

function ProductPill({ type }: { type: string | null | undefined }) {
  if (!type) return <span className="muted">—</span>
  const color = productColor(type)
  return (
    <span style={{
      display:        'inline-flex',
      alignItems:     'center',
      padding:        '2px 8px',
      borderRadius:   999,
      fontSize:       11,
      fontWeight:     600,
      letterSpacing:  '0.04em',
      color:          color,
      background:     `color-mix(in oklch, ${color} 14%, transparent)`,
      border:         `1px solid color-mix(in oklch, ${color} 28%, transparent)`,
    }}>
      {type}
    </span>
  )
}

function CoCell({ name, code }: { name: string; code: string | null | undefined }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: 28, height: 28, borderRadius: 6,
        background: 'var(--ns-surface-2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10.5, fontWeight: 700, color: 'var(--ns-text-muted)',
        letterSpacing: '0.04em', flexShrink: 0,
      }}>
        {initials(name)}
      </div>
      <div style={{ minWidth: 0, overflow: 'hidden' }}>
        <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
          {name}
        </div>
        {code && (
          <div className="mono muted" style={{ fontSize: 11, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
            {code}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function SubscriptionsPage() {
  // ── Data fetches
  const { data: customers = [] } = useQuery<Customer[]>({
    queryKey: ['customers'],
    queryFn:  () => api.get('/customers'),
    staleTime: 5 * 60 * 1000,
  })

  const { data: subscriptions = [], isLoading: subsLoading } = useQuery<Subscription[]>({
    queryKey: ['subscriptions'],
    queryFn:  () => api.get('/subscriptions'),
  })

  // Pick first ACTIVE/PENDING subscription by default; or first in list.
  const [selectedSubId, setSelectedSubId] = useState<number | null>(null)
  const activeSubId = selectedSubId
    ?? subscriptions.find(s => s.status === 'ACTIVE' || s.status === 'PENDING')?.id
    ?? subscriptions[0]?.id
    ?? null

  const currentSub = subscriptions.find(s => s.id === activeSubId) ?? null
  const customer   = customers.find(c => c.id === currentSub?.customerId) ?? null

  // Lines & discounts for the selected subscription.
  const { data: lines = [], isLoading: linesLoading } = useQuery<SubscriptionLine[]>({
    queryKey: ['subscription-lines', activeSubId],
    queryFn:  () => api.get(`/subscriptions/${activeSubId}/lines?status=ACTIVE`),
    enabled:  activeSubId != null,
  })

  const { data: discounts = [] } = useQuery<SubscriptionDiscount[]>({
    queryKey: ['subscription-discounts', activeSubId],
    queryFn:  () => api.get(`/subscriptions/${activeSubId}/discounts`),
    enabled:  activeSubId != null,
  })

  // ── UI state
  const [tab, setTab] = useState<TabKey>('lines')
  const [search, setSearch] = useState('')
  const [productFilter, setProductFilter] = useState<string | null>(null)

  // Modal state — one switch per modal kind. Keeping them flat (vs. a single
  // discriminated union) reads better when several can be triggered from
  // unrelated places.
  const [showNewSub,    setShowNewSub]    = useState(false)
  const [showAddLine,   setShowAddLine]   = useState(false)
  const [showPause,     setShowPause]     = useState(false)
  const [showDiscount,  setShowDiscount]  = useState<{ lineId?: number } | null>(null)
  const [editPriceLine, setEditPriceLine] = useState<SubscriptionLine | null>(null)
  const [endLineTarget, setEndLineTarget] = useState<SubscriptionLine | null>(null)
  // Drawer state — drilldown into a single line's invoice history.
  const [historyLine,   setHistoryLine]   = useState<SubscriptionLine | null>(null)

  const qc = useQueryClient()

  // Status transitions that don't need a form modal — direct mutations with
  // toast feedback. activate is Finance-only on the backend; the button is
  // only visible when status === 'PENDING' so the role mismatch case is
  // surfaced via the 403 toast rather than a silent disable.
  const activateMut = useMutation({
    mutationFn: async () => api.post(`/subscriptions/${currentSub?.id}/activate`, {}),
    onSuccess: () => {
      toast.success('Subscription activated')
      qc.invalidateQueries({ queryKey: ['subscriptions'] })
    },
    onError: e => toast.error(e instanceof ApiError ? e.message : 'Could not activate'),
  })

  const resumeMut = useMutation({
    mutationFn: async () => api.post(`/subscriptions/${currentSub?.id}/resume`, {}),
    onSuccess: () => {
      toast.success('Subscription resumed')
      qc.invalidateQueries({ queryKey: ['subscriptions'] })
    },
    onError: e => toast.error(e instanceof ApiError ? e.message : 'Could not resume'),
  })

  const cancelMut = useMutation({
    mutationFn: async () => api.post(`/subscriptions/${currentSub?.id}/cancel`, {}),
    onSuccess: () => {
      toast.success('Subscription cancelled')
      qc.invalidateQueries({ queryKey: ['subscriptions'] })
    },
    onError: e => toast.error(e instanceof ApiError ? e.message : 'Could not cancel'),
  })

  // ── Derived stats (MRR / ARR / effective discount)
  const stats = useMemo(() => {
    const activeLines = lines.filter(l => l.validTo === null)
    const mrr = activeLines.reduce((s, l) => s + (l.currentUnitPrice ?? 0), 0)
    const arr = mrr * 12
    // Naive effective discount: sum of percentage discounts × mrr + flat sums.
    // (Real calculator lives in the backend; this is a quick UI roll-up.)
    let discountSum = 0
    for (const d of discounts) {
      if (d.ruleType === 'PERCENTAGE' && d.flatPercent != null) {
        discountSum += mrr * (d.flatPercent / 100)
      } else if (d.ruleType === 'FIXED_AMOUNT' && d.flatAmount != null) {
        discountSum += d.flatAmount
      }
    }
    const effPct = mrr > 0 ? (discountSum / mrr) * 100 : 0
    return { activeLineCount: activeLines.length, mrr, arr, discountSum, effPct }
  }, [lines, discounts])

  const productTypes = useMemo(() => {
    const set = new Set<string>()
    for (const l of lines) if (l.productType) set.add(l.productType)
    return Array.from(set).sort()
  }, [lines])

  const filteredLines = useMemo(() => {
    let out = lines
    if (productFilter) out = out.filter(l => l.productType === productFilter)
    if (search) {
      const q = search.toLowerCase()
      out = out.filter(l =>
        l.analyzedCompanyName.toLowerCase().includes(q) ||
        l.analyzedCompanyExternalId.toLowerCase().includes(q) ||
        l.articleNumber.toLowerCase().includes(q),
      )
    }
    return out
  }, [lines, productFilter, search])

  // ── Render

  return (
    <div className="page">
      {/* Page header */}
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Subscriptions</div>
          <h1 className="page-title">
            {customer ? `Subscriptions for ${customer.name}` : 'Subscriptions'}
          </h1>
          <div className="page-subtitle">
            {currentSub
              ? `${stats.activeLineCount} active lines · ${currentSub.billingCycle.toLowerCase()} billing · created ${fmtDate(currentSub.createdAt.slice(0, 10))}`
              : subsLoading ? 'Loading…' : 'Create a subscription to get started.'}
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          {/* Customer picker — lets ops swap which subscription is on screen. */}
          {subscriptions.length > 0 && (
            <select
              className="input"
              value={activeSubId ?? ''}
              onChange={e => setSelectedSubId(Number(e.target.value))}
              style={{ minWidth: 240 }}
            >
              {subscriptions.map(s => (
                <option key={s.id} value={s.id}>
                  {s.customerName} — {s.name}
                </option>
              ))}
            </select>
          )}
          <button className="btn"><Upload size={13} /> Bulk import</button>
          <Link to="/settings/billing" className="btn">
            <Settings size={13} /> Settings
          </Link>
          {currentSub && (
            <Link
              to="/subscriptions/invoice-preview"
              search={{ customerId: currentSub.customerId }}
              className="btn"
            >
              <FileText size={13} /> Generate invoice
            </Link>
          )}
          <button className="btn btn-brand" onClick={() => setShowNewSub(true)}>
            <Plus size={13} /> New subscription
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="stat-grid">
        <StatCard label="Active lines"        value={String(stats.activeLineCount)} accent="ok" footer="On track" />
        <StatCard label="MRR"                 value={fmtMoney(stats.mrr,  currentSub?.currency ?? 'SEK')} accent="ok" footer={`Monthly recurring · ${currentSub?.currency ?? 'SEK'}`} />
        <StatCard label="ARR"                 value={fmtMoney(stats.arr,  currentSub?.currency ?? 'SEK')} accent="ok" footer="Annual run rate" />
        <StatCard label="Effective discount"  value={`${stats.effPct.toFixed(2)}%`} accent="info" footer={`−${fmtMoney(stats.discountSum, currentSub?.currency ?? 'SEK')}/mo`} />
      </div>

      {/* Subscription bar / commitment timeline */}
      {currentSub && (
        <div className="card" style={{ padding: '14px 18px', marginBottom: 14 }}>
          <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
              <Calendar size={15} className="muted" />
              <span>
                <span className="muted" style={{ marginRight: 6 }}>Commitment</span>
                <span className="tabular" style={{ fontWeight: 600 }}>{fmtDate(currentSub.commitmentStart)}</span>
                <span className="muted" style={{ margin: '0 6px' }}>→</span>
                <span className="tabular" style={{ fontWeight: 600 }}>{fmtDate(currentSub.commitmentEnd)}</span>
              </span>
              <span style={{ color: 'var(--ns-text-subtle)', fontSize: 12 }}>
                · {currentSub.billingCycle.toLowerCase()} {currentSub.billingMode.toLowerCase()}
              </span>
              {currentSub.noticePeriodDays != null && (
                <span style={{ color: 'var(--ns-text-subtle)', fontSize: 12 }}>
                  · notice {currentSub.noticePeriodDays} days
                </span>
              )}
            </div>
            <StatusPill status={currentSub.status} />
          </div>
          {/* Visual band: thin bar showing time elapsed within the commitment. */}
          <CommitmentBar
            start={currentSub.commitmentStart}
            end={currentSub.commitmentEnd}
          />
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >{t.label}</button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'lines' && (
        <LinesTab
          lines={filteredLines}
          search={search}
          onSearch={setSearch}
          productTypes={productTypes}
          productFilter={productFilter}
          onProductFilter={setProductFilter}
          currency={currentSub?.currency ?? 'SEK'}
          isLoading={linesLoading}
          onAddLine={() => setShowAddLine(true)}
          onEditPrice={setEditPriceLine}
          onEndLine={setEndLineTarget}
          onOpenHistory={setHistoryLine}
          onBulkApplyDiscount={() => setShowDiscount({})}
        />
      )}
      {tab === 'discounts' && (
        <DiscountsTab
          discounts={discounts}
          stats={stats}
          currency={currentSub?.currency ?? 'SEK'}
          onAddDiscount={() => setShowDiscount({})}
        />
      )}
      {tab === 'history' && activeSubId != null && (
        <HistoryTab subscriptionId={activeSubId} currency={currentSub?.currency ?? 'SEK'} />
      )}
      {tab === 'settings' && currentSub && (
        <SettingsTab
          sub={currentSub}
          onActivate={() => activateMut.mutate()}
          onPause={() => setShowPause(true)}
          onResume={() => resumeMut.mutate()}
          onCancel={() => {
            if (confirm(`Cancel ${currentSub.name}? Every active line will be closed today.`)) cancelMut.mutate()
          }}
        />
      )}

      {/* Modals */}
      {showNewSub && (
        <NewSubscriptionModal
          defaultCustomerId={currentSub?.customerId}
          customers={customers.map(c => ({ id: c.id, name: c.name }))}
          onClose={() => setShowNewSub(false)}
        />
      )}
      {showAddLine && currentSub && (
        <AddLineModal subscription={currentSub} onClose={() => setShowAddLine(false)} />
      )}
      {showPause && currentSub && (
        <PauseSubscriptionModal subscription={currentSub} onClose={() => setShowPause(false)} />
      )}
      {showDiscount && currentSub && (
        <ApplyDiscountModal
          subscription={currentSub}
          lineId={showDiscount.lineId}
          onClose={() => setShowDiscount(null)}
        />
      )}
      {editPriceLine && (
        <ChangePriceModal
          line={editPriceLine}
          currency={currentSub?.currency ?? 'SEK'}
          onClose={() => setEditPriceLine(null)}
        />
      )}
      {endLineTarget && (
        <EndLineModal line={endLineTarget} onClose={() => setEndLineTarget(null)} />
      )}
      {historyLine && (
        <LineInvoiceHistoryDrawer
          line={historyLine}
          currency={currentSub?.currency ?? 'SEK'}
          onClose={() => setHistoryLine(null)}
        />
      )}
    </div>
  )
}


// ── Sub-components ───────────────────────────────────────────────────────────


function StatCard({ label, value, accent, footer }: { label: string; value: string; accent: 'ok' | 'warn' | 'err' | 'info'; footer: string }) {
  const accentColor = {
    ok:   'var(--ns-ok)',
    warn: 'var(--ns-warn)',
    err:  'var(--ns-err)',
    info: 'var(--ns-brand-strong)',
  }[accent]
  return (
    <div className="stat" style={{ borderLeft: `3px solid ${accentColor}` }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value tabular">{value}</div>
      <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>{footer}</div>
    </div>
  )
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, { color: string; label: string }> = {
    PENDING:   { color: 'var(--ns-info)', label: 'Pending'   },
    ACTIVE:    { color: 'var(--ns-ok)',   label: 'Active'    },
    PAUSED:    { color: 'var(--ns-warn)', label: 'Paused'    },
    CANCELLED: { color: 'var(--ns-text-subtle)', label: 'Cancelled' },
  }
  const m = map[status] ?? map.ACTIVE
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap: 6,
      padding: '4px 10px', borderRadius: 999,
      background: `color-mix(in oklch, ${m.color} 14%, transparent)`,
      border:     `1px solid color-mix(in oklch, ${m.color} 30%, transparent)`,
      color:       m.color, fontSize: 11.5, fontWeight: 600,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: m.color }} /> {m.label}
    </span>
  )
}

function CommitmentBar({ start, end }: { start: string; end: string | null }) {
  if (!end) return null
  const now = new Date()
  const s = new Date(start)
  const e = new Date(end)
  if (e <= s) return null
  const pct = Math.max(0, Math.min(100, ((now.getTime() - s.getTime()) / (e.getTime() - s.getTime())) * 100))
  return (
    <div style={{
      marginTop: 10, height: 6, borderRadius: 999,
      background: 'var(--ns-surface-2)',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        width: `${pct}%`,
        background: 'linear-gradient(90deg, var(--ns-brand-strong), color-mix(in oklch, var(--ns-brand-strong) 70%, white))',
      }} />
    </div>
  )
}

function LinesTab({
  lines, search, onSearch, productTypes, productFilter, onProductFilter,
  currency, isLoading, onAddLine, onEditPrice, onEndLine, onOpenHistory, onBulkApplyDiscount,
}: {
  lines: SubscriptionLine[]
  search: string
  onSearch: (s: string) => void
  productTypes: string[]
  productFilter: string | null
  onProductFilter: (p: string | null) => void
  currency: string
  isLoading: boolean
  onAddLine: () => void
  onEditPrice: (l: SubscriptionLine) => void
  onEndLine: (l: SubscriptionLine) => void
  onOpenHistory: (l: SubscriptionLine) => void
  onBulkApplyDiscount: () => void
}) {
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const allChecked = lines.length > 0 && selected.size === lines.length
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(lines.map(l => l.id)))
  const toggleOne = (id: number) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id); else next.add(id)
    setSelected(next)
  }

  return (
    <>
      <div className="card" style={{ padding: 12, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', minWidth: 260 }}>
          <Search size={14} style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--ns-text-muted)',
          }} />
          <input
            className="input"
            placeholder="Search company or article…"
            value={search}
            onChange={e => onSearch(e.target.value)}
            style={{ paddingLeft: 30, minWidth: 260 }}
          />
        </div>
        <FilterChipLocal
          label="Product type"
          value={productFilter}
          options={productTypes}
          onChange={onProductFilter}
        />
        <div style={{ flex: 1 }} />
        <button className="btn btn-brand" onClick={onAddLine}><Plus size={13} /> Add line</button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input type="checkbox" checked={allChecked} onChange={toggleAll} />
              </th>
              <th>Analyzed company</th>
              <th>Product</th>
              <th>Article</th>
              <th style={{ textAlign: 'right' }}>Unit price</th>
              <th>Valid from</th>
              <th>Valid to</th>
              <th>Last changed</th>
              <th style={{ width: 110 }}></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center' }} className="muted">Loading…</td></tr>
            )}
            {!isLoading && lines.length === 0 && (
              <tr><td colSpan={9} style={{ padding: 36, textAlign: 'center' }} className="muted">
                No subscription lines yet. Click <strong>Add line</strong> to attach the first one.
              </td></tr>
            )}
            {lines.map(l => (
              <tr key={l.id} className={selected.has(l.id) ? 'selected' : ''}>
                <td><input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleOne(l.id)} /></td>
                <td><CoCell name={l.analyzedCompanyName} code={l.analyzedCompanyExternalId} /></td>
                <td><ProductPill type={l.productType} /></td>
                <td className="mono" style={{ fontSize: 12 }}>{l.articleNumber}</td>
                <td className="tabular" style={{ textAlign: 'right', fontWeight: 600 }}>
                  {fmtMoney(l.currentUnitPrice, currency)}
                </td>
                <td className="tabular" style={{ fontSize: 12.5 }}>{fmtDate(l.validFrom)}</td>
                <td className="tabular" style={{ fontSize: 12.5 }}>{l.validTo ? fmtDate(l.validTo) : '—'}</td>
                <td className="muted" style={{ fontSize: 11.5 }}>
                  {l.lastChangedBy ?? '—'}
                </td>
                <td>
                  <div className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                    <button className="btn btn-icon btn-sm" title="Invoice history" onClick={() => onOpenHistory(l)}>
                      <Receipt size={12} />
                    </button>
                    <button className="btn btn-icon btn-sm" title="Change price" onClick={() => onEditPrice(l)}>
                      <DollarSign size={12} />
                    </button>
                    <button className="btn btn-icon btn-sm" title="End" onClick={() => onEndLine(l)}>
                      <X size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer counters */}
      <div className="muted" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11.5 }}>
        <span>Showing {lines.length} active lines</span>
        <span>Sticky header · saved presets coming soon</span>
      </div>

      {/* Bulk-bar (only when selection exists) */}
      {selected.size > 0 && (
        <BulkBar
          count={selected.size}
          onApplyDiscount={onBulkApplyDiscount}
        />
      )}
    </>
  )
}


function FilterChipLocal({
  label, value, options, onChange,
}: { label: string; value: string | null; options: string[]; onChange: (v: string | null) => void }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <button
        className="btn"
        onClick={() => setOpen(o => !o)}
        style={value ? {
          background: 'var(--ns-brand-soft)',
          borderColor: 'color-mix(in oklch, var(--ns-brand-strong) 35%, white)',
          color: 'var(--ns-brand-strong)',
        } : {}}
      >
        {label}: <strong style={{ marginLeft: 4 }}>{value ?? 'Any'}</strong>
        <ChevronDown size={12} />
      </button>
      {open && (
        <div className="card" style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 10,
          minWidth: 180, padding: 4, boxShadow: 'var(--ns-shadow)',
        }}>
          <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'flex-start' }}
            onClick={() => { onChange(null); setOpen(false) }}>Any</button>
          {options.map(o => (
            <button key={o} className="btn btn-ghost" style={{ width: '100%', justifyContent: 'flex-start' }}
              onClick={() => { onChange(o); setOpen(false) }}>{o}</button>
          ))}
        </div>
      )}
    </div>
  )
}


function BulkBar({ count, onApplyDiscount }: { count: number; onApplyDiscount: () => void }) {
  // PR 2 ships a single bulk action (Apply discount). The Bulk-end-lines flow
  // is a follow-up — there's no /lines/end-bulk endpoint yet, and confirming a
  // multi-line cancellation deserves its own modal with a date picker.
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      background: 'var(--ns-text)', color: 'var(--ns-surface)',
      padding: '10px 16px', borderRadius: 12,
      display: 'flex', alignItems: 'center', gap: 12,
      boxShadow: 'var(--ns-shadow)', zIndex: 20,
    }}>
      <span style={{ fontSize: 12, fontWeight: 600 }}>{count} lines selected</span>
      <button className="btn btn-ghost btn-sm" style={{ color: 'inherit' }} onClick={onApplyDiscount}>
        <Tag size={12} /> Apply discount…
      </button>
    </div>
  )
}


function DiscountsTab({ discounts, stats, currency, onAddDiscount }: {
  discounts: SubscriptionDiscount[]
  stats: { mrr: number; discountSum: number; effPct: number }
  currency: string
  onAddDiscount: () => void
}) {
  return (
    <>
      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', marginBottom: 12 }}>
        <StatCard label="Gross total"    value={fmtMoney(stats.mrr, currency)}        accent="info" footer="Pre-discount" />
        <StatCard label="Total discount" value={`−${fmtMoney(stats.discountSum, currency)}`} accent="ok" footer={`${stats.effPct.toFixed(2)}% effective`} />
        <StatCard label="Net to invoice" value={fmtMoney(stats.mrr - stats.discountSum, currency)} accent="ok" footer="Per month" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 12 }}>
        {discounts.length === 0 && (
          <div className="card" style={{ padding: 24, textAlign: 'center' }}>
            <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>No discounts attached yet.</div>
            <button className="btn" onClick={onAddDiscount}><Plus size={13} /> Add discount</button>
          </div>
        )}
        {discounts.map(d => (
          <DiscountCard key={d.id} discount={d} currency={currency} />
        ))}
        {discounts.length > 0 && (
          <button className="card btn-ghost"
            style={{ padding: 24, border: '1px dashed var(--ns-border-strong)', minHeight: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            onClick={onAddDiscount}>
            <Plus size={14} /> Add discount
          </button>
        )}
      </div>
    </>
  )
}


function DiscountCard({ discount, currency }: { discount: SubscriptionDiscount; currency: string }) {
  const accent = discount.subscriptionLineId
    ? 'oklch(0.62 0.13 50)'           // line-scoped → clem orange
    : 'var(--ns-brand-strong)'        // subscription-scoped → teal

  const ruleTypeLabel: Record<string, string> = {
    VOLUME_TIER:  'Volume tier',
    PERCENTAGE:   'Flat percent',
    FIXED_AMOUNT: 'Fixed amount',
  }

  let summary = '—'
  if (discount.ruleType === 'PERCENTAGE' && discount.flatPercent != null) {
    summary = `${discount.flatPercent}% off`
  } else if (discount.ruleType === 'FIXED_AMOUNT' && discount.flatAmount != null) {
    summary = `${fmtMoney(discount.flatAmount, discount.flatAmountCurrency ?? currency)} off`
  } else if (discount.ruleType === 'VOLUME_TIER') {
    summary = 'Tier bands (see rule)'
  }

  return (
    <div className="card" style={{ padding: 16, borderLeft: `3px solid ${accent}` }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{discount.ruleName}</div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
            {ruleTypeLabel[discount.ruleType] ?? discount.ruleType}
            {discount.productType && ` · ${discount.productType}`}
            {discount.subscriptionLineId && ' · single line'}
          </div>
        </div>
        <span className="badge badge-ok"><span className="dot dot-ok" /> Active</span>
      </div>
      <div className="mono" style={{ marginTop: 12, fontSize: 11.5, background: 'var(--ns-surface-2)', padding: '8px 10px', borderRadius: 6 }}>
        {summary}
      </div>
      <div className="muted" style={{ fontSize: 11.5, marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Calendar size={12} /> {fmtDate(discount.validFrom)} → {discount.validTo ? fmtDate(discount.validTo) : 'open-ended'}
      </div>
      <div className="row" style={{ gap: 6, marginTop: 12 }}>
        <button className="btn btn-sm">Edit</button>
        <button className="btn btn-sm">View calc</button>
        <button className="btn btn-sm btn-ghost" style={{ color: 'var(--ns-err)', marginLeft: 'auto' }}>Remove</button>
      </div>
    </div>
  )
}


function HistoryTab({ subscriptionId, currency }: { subscriptionId: number; currency: string }) {
  const { data: events = [], isLoading } = useQuery<SubscriptionEvent[]>({
    queryKey: ['subscription-events', subscriptionId],
    queryFn:  () => api.get(`/subscriptions/${subscriptionId}/events?limit=200`),
  })

  if (isLoading) {
    return (
      <div className="card" style={{ padding: 36, textAlign: 'center' }} >
        <span className="muted">Loading…</span>
      </div>
    )
  }
  if (events.length === 0) {
    return (
      <div className="card" style={{ padding: 36, textAlign: 'center' }}>
        <Activity size={28} className="muted" style={{ margin: '0 auto 10px' }} />
        <div style={{ fontWeight: 600, marginBottom: 6 }}>No events yet</div>
        <div className="muted" style={{ fontSize: 13 }}>
          Subscription events (line add, price change, trial extend, etc.) appear here as the module is used.
        </div>
      </div>
    )
  }

  return (
    <div className="card" style={{ padding: 0 }}>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {events.map(e => (
          <li key={e.id} style={{
            display: 'grid',
            gridTemplateColumns: '36px 1fr auto',
            gap: 12,
            padding: '12px 18px',
            borderBottom: '1px solid var(--ns-border)',
          }}>
            <EventIcon eventType={e.eventType} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{eventTitle(e)}</div>
              <EventDetail event={e} currency={currency} />
              <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
                {e.createdByName ?? 'system'} · {fmtDate(e.createdAt.slice(0, 10))}
              </div>
            </div>
            <div className="muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap', alignSelf: 'start' }}>
              {fmtRelative(e.createdAt)}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}


function EventIcon({ eventType }: { eventType: SubscriptionEvent['eventType'] }) {
  // Tone per event family — keep this tight so the timeline reads at a glance.
  const tone: Record<string, { bg: string; fg: string }> = {
    SUBSCRIPTION_CREATED:   { bg: 'var(--ns-ok)',          fg: 'white' },
    SUBSCRIPTION_ACTIVATED: { bg: 'var(--ns-ok)',          fg: 'white' },
    SUBSCRIPTION_RESUMED:   { bg: 'var(--ns-ok)',          fg: 'white' },
    SUBSCRIPTION_PAUSED:    { bg: 'var(--ns-warn)',        fg: 'white' },
    SUBSCRIPTION_CANCELLED: { bg: 'var(--ns-err)',         fg: 'white' },
    LINE_ADDED:             { bg: 'var(--ns-brand-strong)', fg: 'white' },
    LINES_BULK_ADDED:       { bg: 'var(--ns-brand-strong)', fg: 'white' },
    LINE_PRICE_CHANGED:     { bg: 'oklch(0.55 0.16 258)',  fg: 'white' },
    LINE_ENDED:             { bg: 'var(--ns-err)',         fg: 'white' },
    LINE_TRIAL_SET:         { bg: 'oklch(0.55 0.15 295)',  fg: 'white' },
    DISCOUNT_ATTACHED:      { bg: 'oklch(0.62 0.13 50)',   fg: 'white' },
    DISCOUNT_UPDATED:       { bg: 'oklch(0.62 0.13 50)',   fg: 'white' },
    DISCOUNT_ENDED:         { bg: 'var(--ns-text-muted)',  fg: 'white' },
    PAUSE_RECORDED:         { bg: 'var(--ns-warn)',        fg: 'white' },
    PAUSE_ENDED:            { bg: 'var(--ns-text-muted)',  fg: 'white' },
  }
  const t = tone[eventType] ?? { bg: 'var(--ns-text-muted)', fg: 'white' }
  return (
    <div style={{
      width: 32, height: 32, borderRadius: 999,
      background: t.bg, color: t.fg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 700,
    }}>
      <Activity size={14} />
    </div>
  )
}


function eventTitle(e: SubscriptionEvent): string {
  const titles: Record<string, string> = {
    SUBSCRIPTION_CREATED:   'Subscription created',
    SUBSCRIPTION_ACTIVATED: 'Subscription activated',
    SUBSCRIPTION_PAUSED:    'Subscription paused',
    SUBSCRIPTION_RESUMED:   'Subscription resumed',
    SUBSCRIPTION_CANCELLED: 'Subscription cancelled',
    LINE_ADDED:             'Line added',
    LINES_BULK_ADDED:       'Lines added (bulk)',
    LINE_PRICE_CHANGED:     'Price changed',
    LINE_ENDED:             'Line ended',
    LINE_TRIAL_SET:         'Trial updated',
    DISCOUNT_ATTACHED:      'Discount attached',
    DISCOUNT_UPDATED:       'Discount updated',
    DISCOUNT_ENDED:         'Discount ended',
    PAUSE_RECORDED:         'Pause recorded',
    PAUSE_ENDED:            'Pause ended',
  }
  return titles[e.eventType] ?? e.eventType
}


function EventDetail({ event, currency }: { event: SubscriptionEvent; currency: string }) {
  const p = event.payload as Record<string, unknown> | null
  if (!p) return null

  const get = (k: string) => p[k] as string | number | null | undefined

  // Per-event detail render — keep these short; the timeline is a glance medium.
  switch (event.eventType) {
    case 'SUBSCRIPTION_CREATED':
      return (
        <div className="muted" style={{ fontSize: 12.5 }}>
          {String(get('name'))} · {String(get('billingCycle'))} ·
          {' '}commitment {fmtDate(get('commitmentStart') as string ?? null)} → {get('commitmentEnd') ? fmtDate(get('commitmentEnd') as string) : 'perpetual'}
        </div>
      )

    case 'SUBSCRIPTION_ACTIVATED':
    case 'SUBSCRIPTION_PAUSED':
    case 'SUBSCRIPTION_RESUMED':
    case 'SUBSCRIPTION_CANCELLED':
      return (
        <div className="muted" style={{ fontSize: 12.5 }}>
          {String(get('from'))} → <strong>{String(get('to'))}</strong>
        </div>
      )

    case 'LINE_ADDED':
      return (
        <div className="muted" style={{ fontSize: 12.5 }}>
          {String(get('analyzedCompanyName') ?? '—')} · <span className="mono">{String(get('articleNumber') ?? '—')}</span> · {fmtMoney(get('unitPrice') as number ?? null, currency)}/mo
        </div>
      )

    case 'LINES_BULK_ADDED':
      return (
        <div className="muted" style={{ fontSize: 12.5 }}>
          {String(get('count'))} lines added at once
        </div>
      )

    case 'LINE_PRICE_CHANGED':
      return (
        <div className="muted" style={{ fontSize: 12.5 }}>
          <span className="tabular">{fmtMoney(get('oldUnitPrice') as number ?? null, currency)}</span>
          {' → '}
          <strong className="tabular">{fmtMoney(get('newUnitPrice') as number ?? null, currency)}</strong>
          {' · '}{String(get('changeReason'))} · from {fmtDate(get('validFrom') as string ?? null)}
        </div>
      )

    case 'LINE_ENDED':
      return <div className="muted" style={{ fontSize: 12.5 }}>Ends {fmtDate(get('validTo') as string ?? null)}</div>

    case 'LINE_TRIAL_SET':
      if (p.newTrialEndDate) {
        return <div className="muted" style={{ fontSize: 12.5 }}>Trial through {fmtDate(get('newTrialEndDate') as string)}</div>
      }
      return <div className="muted" style={{ fontSize: 12.5 }}>Trial cleared</div>

    case 'DISCOUNT_ATTACHED':
      return (
        <div className="muted" style={{ fontSize: 12.5 }}>
          {String(get('ruleName') ?? '—')} ({String(get('ruleType'))}) ·
          {' '}{get('subscriptionLineId') ? 'line-scoped' : (get('productArticleId') ? 'product-scoped' : 'subscription-scoped')}
        </div>
      )

    case 'DISCOUNT_UPDATED':
      return (
        <div className="muted mono" style={{ fontSize: 11.5 }}>
          {JSON.stringify(p.changedFields)}
        </div>
      )

    case 'DISCOUNT_ENDED':
    case 'PAUSE_ENDED':
      return <div className="muted" style={{ fontSize: 12.5 }}>Ended {fmtDate(get('validTo') as string ?? null)}</div>

    case 'PAUSE_RECORDED':
      return (
        <div className="muted" style={{ fontSize: 12.5 }}>
          From {fmtDate(get('validFrom') as string ?? null)} {get('validTo') ? `→ ${fmtDate(get('validTo') as string)}` : '· open-ended'}
          {get('reason') && ` · ${String(get('reason'))}`}
        </div>
      )

    default:
      return null
  }
}


function fmtRelative(isoTs: string): string {
  const ms = Date.now() - new Date(isoTs).getTime()
  const min = Math.floor(ms / 60_000)
  if (min < 1)    return 'just now'
  if (min < 60)   return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24)    return `${hr}h ago`
  const d = Math.floor(hr / 24)
  if (d < 30)     return `${d}d ago`
  return new Date(isoTs).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
}


function SettingsTab({
  sub, onActivate, onPause, onResume, onCancel,
}: {
  sub: Subscription
  onActivate: () => void
  onPause:    () => void
  onResume:   () => void
  onCancel:   () => void
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
      <div className="card" style={{ padding: 20 }}>
        <div style={{ fontWeight: 600, marginBottom: 14 }}>Subscription metadata</div>
        <SettingsRow label="Name">{sub.name}</SettingsRow>
        <SettingsRow label="Contract reference"><span className="mono">{sub.contractReference ?? '—'}</span></SettingsRow>
        <SettingsRow label="PO number"><span className="mono">{sub.poNumber ?? '—'}</span></SettingsRow>
        <SettingsRow label="Commitment"><span className="tabular">{fmtDate(sub.commitmentStart)} → {fmtDate(sub.commitmentEnd)}</span></SettingsRow>
        <SettingsRow label="Notice period">{sub.noticePeriodDays != null ? `${sub.noticePeriodDays} days` : '—'}</SettingsRow>
        <SettingsRow label="Billing cycle">{sub.billingCycle}</SettingsRow>
        <SettingsRow label="Billing mode">{sub.billingMode}</SettingsRow>
        <SettingsRow label="Currency">{sub.currency}</SettingsRow>
        <SettingsRow label="Payment terms">{sub.paymentTermsDays != null ? `${sub.paymentTermsDays} days` : 'Customer default'}</SettingsRow>
        <SettingsRow label="Trial end"><span className="tabular">{fmtDate(sub.trialEndDate)}</span></SettingsRow>
        <SettingsRow label="Invoice grouping">{sub.invoiceGrouping === 'ONE_INVOICE' ? 'One invoice per customer' : 'Separate per product type'}</SettingsRow>
      </div>
      <div className="card" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Lifecycle</div>
        {sub.status === 'PENDING' && (
          <button className="btn btn-brand" onClick={onActivate}>Activate subscription</button>
        )}
        {sub.status === 'ACTIVE'  && (
          <button className="btn" onClick={onPause}>Pause subscription</button>
        )}
        {sub.status === 'PAUSED'  && (
          <button className="btn btn-brand" onClick={onResume}>Resume subscription</button>
        )}
        <div style={{ flex: 1 }} />
        <div className="muted" style={{ fontSize: 11.5, marginTop: 12 }}>Danger zone</div>
        {sub.status !== 'CANCELLED' && (
          <button className="btn" style={{ color: 'var(--ns-err)', borderColor: 'var(--ns-err)' }} onClick={onCancel}>
            Cancel subscription
          </button>
        )}
      </div>
    </div>
  )
}


function SettingsRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 16, padding: '8px 0', fontSize: 13, alignItems: 'baseline', borderBottom: '1px solid var(--ns-border)' }}>
      <div className="muted">{label}</div>
      <div>{children}</div>
    </div>
  )
}
